import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { resolveImg } from "@/lib/api";
import { AmazonLogo } from "@/components/site/brands";
import { ArrowRight } from "lucide-react";

function Particles() {
  const dots = Array.from({ length: 18 });
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {dots.map((_, i) => {
        const left = Math.random() * 100;
        const size = 3 + Math.random() * 6;
        const dur = 8 + Math.random() * 10;
        const delay = Math.random() * 8;
        return (
          <span
            key={i}
            style={{
              left: `${left}%`,
              bottom: "-10px",
              width: size,
              height: size,
              animation: `rise ${dur}s linear ${delay}s infinite`,
              background: i % 3 === 0 ? "rgba(212,175,55,0.55)" : "rgba(10,10,10,0.12)",
            }}
            className="absolute rounded-full"
          />
        );
      })}
    </div>
  );
}

function HeroBook({ book, anim, delay, isNew = false, orderClass = "", widthClass }) {
  if (!book) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay }}
      className={`snap-center shrink-0 flex flex-col items-center ${orderClass}`}
    >
      {/* Static positioning context. The badge is absolutely positioned above the
         cover and NEVER moves. Only the <img> floats gently (see .hero-anim-* CSS). */}
      <div className="hero-float relative flex flex-col items-center">
        {isNew && (
          <span className="hero-badge" data-testid="hero-new-release-badge">
            New Release
          </span>
        )}
        <img
          src={resolveImg(book.cover_image)}
          alt={`${book.title} book cover by Kate Mercer`}
          loading="eager"
          decoding="async"
          className={`book-shadow img-crisp rounded-lg block h-auto ${anim} ${widthClass}`}
        />
      </div>
      <a
        href={book.amazon_link}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`hero-buy-${book.slug}`}
        className="btn-gold btn-pill btn-pill-sm mt-6 w-full max-w-[210px]"
      >
        Buy on Amazon <ArrowRight size={16} />
      </a>
      <div className="mt-2.5 flex items-center gap-1.5 opacity-70">
        <span className="text-[11px] text-gray-500">Available on</span>
        <AmazonLogo className="h-3.5 w-auto" />
      </div>
    </motion.div>
  );
}

export default function Hero({ config, books }) {
  const bySlug = Object.fromEntries((books || []).map((b) => [b.slug, b]));
  const first = bySlug["first-class-student"];
  const reflex = bySlug["the-ai-reflex"];
  const graduate = bySlug["graduate-game-ai-edition"];

  const scrollRef = useRef(null);
  const [active, setActive] = useState(0);
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / (el.clientWidth * 0.55));
    setActive(Math.max(0, Math.min(2, idx)));
  };

  const std = "hero-cover w-[150px] sm:w-[190px] lg:w-[186px] xl:w-[204px]";
  const big = "hero-cover-big w-[158px] sm:w-[196px] lg:w-[194px] xl:w-[212px]";

  return (
    <section id="top" className="relative pt-28 sm:pt-32 pb-16 sm:pb-24 overflow-hidden grain">
      <div className="absolute -top-24 -left-24 w-[440px] h-[440px] rounded-full blob"
           style={{ background: "radial-gradient(circle, rgba(212,175,55,0.20), transparent 70%)" }} />
      <div className="absolute top-40 -right-24 w-[520px] h-[520px] rounded-full blob"
           style={{ background: "radial-gradient(circle, rgba(59,130,246,0.10), transparent 70%)", animationDelay: "-6s" }} />
      <Particles />

      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 text-center">
        <motion.span
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="inline-block text-[11px] sm:text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-6"
        >
          Education · Careers · Artificial Intelligence
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.05 }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight"
        >
          {(config?.hero_headline || "Learn Smarter. Think Better. Stay Ahead.")
            .split(/(?<=\.)\s+/)
            .filter(Boolean)
            .map((line, i) => (
              <span key={i} className="block">{line}</span>
            ))}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-6 text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed"
        >
          {config?.hero_subheading}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-8 flex flex-wrap gap-3 justify-center"
        >
          <a href="#books" className="btn-ink btn-pill" data-testid="hero-explore-books">
            Explore the Books
          </a>
          <a href="#about" className="btn-pill border border-gray-300 hover:border-black transition-colors">
            Meet Kate
          </a>
        </motion.div>

        {/* Three-book showcase: swipeable carousel on mobile, centred row on desktop */}
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="hero-showcase mt-14 sm:mt-16 pt-16 md:pt-0 flex items-end justify-start md:justify-center gap-8 sm:gap-10 lg:gap-10 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none no-scrollbar px-1 pb-4"
        >
          <HeroBook book={first} anim="hero-anim-a" delay={0.3} widthClass={std} orderClass="order-2 md:order-1" />
          <HeroBook book={reflex} anim="hero-anim-b" delay={0.4} isNew widthClass={big} orderClass="order-1 md:order-2" />
          <HeroBook book={graduate} anim="hero-anim-c" delay={0.5} widthClass={std} orderClass="order-3 md:order-3" />
        </div>

        {/* Swipe cue — mobile only, hidden once the desktop row is shown */}
        <div className="mt-1 flex items-center justify-center gap-2 md:hidden" data-testid="hero-swipe-dots">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                active === i ? "w-5 bg-gold" : "w-1.5 bg-gray-300"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
